import React, { useState, useEffect } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Grid, Button, TextField,
  MenuItem, Alert, CircularProgress, IconButton, Typography,
} from '@mui/material';
import { Payments, Store, AccountBalance, PersonPin, Close, Add, CheckRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { adminCreateCashPayment } from 'api/wallet';
import { GetFinalStationList } from 'api/stations';
import { GetLatestRate } from 'api/currencyExchangeRate';
import useStationRefresh from 'hooks/useStationRefresh';
import EvidenceUploadButton from 'components/EvidenceUploadButton';
import { sectionLabelSx } from 'components/bento/BentoFormControls';

const CURRENCIES = ['THB', 'LAK', 'USD'];
const CURRENCY_SYMBOLS = { THB: '฿', LAK: '₭', USD: '$' };
const MONO = "'Space Grotesk', ui-monospace, monospace";

// Floating-point slack for THB comparisons — mirrors the backend's EPSILON_THB
// (controllers/financial.js) and ShippingPaymentDialog.jsx's own constant.
const EPSILON_THB = 1e-6;

// Base = THB: rate.USD = THB per 1 USD, rate.LAK = LAK per 1 THB. Mirrors
// ShippingPaymentDialog.jsx and the mobile WalletTopupScreen.tsx.
const toTHB = (val, cur, rate) => {
  if (!rate || cur === 'THB') return val;
  if (cur === 'USD') return val * (rate.USD || 0);
  if (cur === 'LAK') return val / (rate.LAK || 1);
  return val;
};
const fromTHB = (val, cur, rate) => {
  if (!rate || cur === 'THB') return val;
  if (cur === 'USD') return val / (rate.USD || 1);
  if (cur === 'LAK') return val * (rate.LAK || 0);
  return val;
};
// Rounded version of fromTHB for an amount actually handed over/back as
// physical cash — LAK notes only come in multiples of 1,000, THB/USD round to
// the nearest cent. Mirrors ShippingPaymentDialog.jsx's fromTHB.
const roundedFromTHB = (val, cur, rate) => {
  if (!rate) return 0;
  const n = val || 0;
  if (cur === 'LAK') return Math.round((n * (rate.LAK || 0)) / 1000) * 1000;
  return Math.round(fromTHB(n, cur, rate) * 100) / 100;
};
// Whole-unit version for auto-deriving Amount Due from a payment amount — a due
// amount is normally quoted as a round number, so the odd cents left over from
// converting through the exchange rate (e.g. 704,000 LAK → ฿1,020.29) get
// rounded off rather than shown. Mirrors mobile WalletTopupScreen.tsx.
const roundedWholeFromTHB = (val, cur, rate) => {
  if (!rate) return 0;
  const n = val || 0;
  if (cur === 'LAK') return Math.round((n * (rate.LAK || 0)) / 1000) * 1000;
  return Math.round(fromTHB(n, cur, rate));
};
// Half of a currency's smallest practical denomination, expressed in THB — THB
// and USD round to the nearest cent (step 0.01), LAK to the nearest 1,000-note
// (step 1,000). Used as per-line slack when comparing paid vs. due, since
// converting a rounded amount back through the exchange rate reintroduces up
// to half a step of drift that isn't a real under/overpayment (e.g. ฿1,020
// due paid as $31.88 at a rate of 32 lands at ฿1,020.16). Mirrors the
// backend's halfRoundingStepTHB (controllers/wallet.js) and mobile
// WalletTopupScreen.tsx.
const halfRoundingStepTHB = (cur, rate) => {
  if (!rate) return 0;
  if (cur === 'LAK') return 500 / (rate.LAK || 1);
  if (cur === 'USD') return 0.005 * (rate.USD || 0);
  return 0.005; // THB
};
// Auto-fill the LAST change line with whatever remains of the change due after
// every other line — same convention as ShippingPaymentDialog.jsx's
// recomputeAutoChangeLine. Only the last line auto-fills; earlier lines and any
// line staff has typed into directly are left alone.
const recomputeAutoLine = (lines, dueTHB, rate) => {
  if (lines.length === 0) return lines;
  const lastIdx = lines.length - 1;
  const last = lines[lastIdx];
  if (!last.auto) return lines;
  const othersTHB = lines.slice(0, lastIdx).reduce((s, l) => s + toTHB(parseFloat(l.amount) || 0, l.currency, rate), 0);
  const remainingTHB = Math.max(0, dueTHB - othersTHB);
  const suggested = rate ? roundedFromTHB(remainingTHB, last.currency, rate) : 0;
  const amountStr = suggested > 0 ? String(suggested) : '';
  if (amountStr === last.amount) return lines;
  const next = lines.slice();
  next[lastIdx] = { ...last, amount: amountStr };
  return next;
};
const emptyChangeLine = () => ({ source: 'cash', currency: 'THB', amount: '', auto: true });
// One payment line = one currency + amount + evidence file; a method can hold
// several lines (e.g. cash in THB plus cash in LAK). See ShippingPaymentDialog.jsx.
const emptyLine = (currency = 'THB') => ({ currency, amount: '', file: null });
const emptyMethod = () => ({ enabled: false, lines: [emptyLine()] });
const methodTHB = (m, rate) => (m.enabled ? m.lines.reduce((s, l) => s + toTHB(parseFloat(l.amount) || 0, l.currency, rate), 0) : 0);
const methodCurrencies = (m) => (m.enabled ? m.lines.map((l) => l.currency) : []);

const METHOD_META = {
  cash: { labelKey: 'wallet.admin.paymentSourceCashStore', icon: Store, backendMethod: 'cash_store' },
  bank: { labelKey: 'wallet.admin.paymentSourceBankTransfer', icon: AccountBalance, backendMethod: 'bank_transfer' },
  staffAdvance: { labelKey: 'wallet.admin.paymentSourceStaffAdvance', icon: PersonPin, backendMethod: 'staff_advance' },
};

// Top-up dialog for a customer whose wallet account is already known (reached from
// the account transactions screen) — skips the customer-search step the old
// standalone Cash Payment page (now removed) used to require. UX mirrors
// ShippingPaymentDialog.jsx (WhatsApp pickup/delivery collection): a prominent
// "amount due" figure, combinable payment-method tiles that auto-prefill to
// cover it, and an auto-appearing change section on overpayment — instead of
// the old single-payment-source + manual "give change" toggle model.
const WalletTopupDialog = ({ open, onClose, account, onSuccess, defaultAmount, defaultCurrency }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const accentLight = theme.palette.secondary.light;
  const accentDark = theme.palette.secondary.dark;
  const paper = theme.palette.background.paper;
  const BORDER = theme.palette.divider;
  const tint = alpha(accent, 0.06);

  // Amount due — the target amount to credit to the wallet. Pre-filled from
  // Pay Debt (defaultAmount/defaultCurrency); blank for a plain Top Up.
  const [amount, setAmount] = useState('');
  // Amount due auto-follows the payment-method amounts (converted into
  // `currency`) as long as it hasn't been typed into directly — lets staff
  // enter what was physically received (e.g. LAK cash) and see the equivalent
  // due amount computed for them instead of doing the math by hand. A
  // prefilled Pay Debt amount is an explicit due amount, so it starts out of
  // auto mode. Mirrors mobile WalletTopupScreen.tsx.
  const [dueAuto, setDueAuto] = useState(!defaultAmount);
  const [currency, setCurrency] = useState('THB');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stationList, setStationList] = useState([]);
  const [stationId, setStationId] = useState('');
  const [rate, setRate] = useState(null);
  const { user: _user, ready } = useStationRefresh();

  // Bank transfer + cash at store are freely combinable (split payment); staff
  // advance has no drawer/bank account to combine against, so it's mutually
  // exclusive with the other two — see toggleMethod.
  const [cash, setCash] = useState(emptyMethod);
  const [bank, setBank] = useState(emptyMethod);
  const [staffAdvance, setStaffAdvance] = useState(emptyMethod);

  // Change given back for an overpayment — appears automatically (no manual
  // toggle) once paid exceeds due. Defaults to physical cash change; staff can
  // switch a line to crediting the wallet instead.
  const [changeLines, setChangeLines] = useState([emptyChangeLine()]);

  useEffect(() => {
    if (!open) return;
    setAmount(defaultAmount ?? '');
    setDueAuto(!defaultAmount);
    setCurrency(defaultCurrency ?? 'THB');
    setDescription('');
    setStationId('');
    setCash(emptyMethod());
    setBank(emptyMethod());
    setStaffAdvance(emptyMethod());
    setChangeLines([emptyChangeLine()]);
    setError('');
    GetLatestRate().then((res) => setRate(res?.data?._id ? res.data : null)).catch(() => setRate(null));
  }, [open, account, defaultAmount, defaultCurrency]);

  useEffect(() => {
    GetFinalStationList().then((res) => {
      if (res && res.success) {
        const arr = Array.isArray(res.data) ? res.data : [];
        const userStations = Array.isArray(_user?.stations) ? _user.stations : [];
        const validStation = arr.filter((finalStation) =>
          userStations.some((userStation) => userStation.id === finalStation.id)
        );
        setStationList(validStation.length > 0 ? validStation : arr);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const amt = parseFloat(amount) || 0;
  const dueTHB = toTHB(amt, currency, rate);

  const paidTHB = methodTHB(cash, rate) + methodTHB(bank, rate) + methodTHB(staffAdvance, rate);
  const anyMethodEnabled = cash.enabled || bank.enabled || staffAdvance.enabled;

  // Rounding slack for every currency involved (due amount + each payment
  // line), not just LAK — mirrors the backend's own tolerance in
  // createSplitCashPayment. See halfRoundingStepTHB.
  const toleranceTHB = [currency, ...methodCurrencies(cash), ...methodCurrencies(bank), ...methodCurrencies(staffAdvance)]
    .reduce((sum, c) => sum + halfRoundingStepTHB(c, rate), 0) + EPSILON_THB;
  const diffTHB = paidTHB - dueTHB;
  const changeDueTHB = diffTHB > toleranceTHB ? diffTHB : 0;
  const underpaid = anyMethodEnabled && diffTHB < -toleranceTHB;
  // Display-only: within the rounding tolerance, show "Paid now" as matching
  // Amount due rather than the raw converted figure — that tiny gap is just
  // currency-conversion rounding, not a real under/overpayment.
  const displayPaidTHB = Math.abs(diffTHB) <= toleranceTHB ? dueTHB : paidTHB;

  const changeLinesTHB = changeLines.reduce((s, l) => s + toTHB(parseFloat(l.amount) || 0, l.currency, rate), 0);
  // Separate tolerance for the change comparison — must sum the CHANGE lines'
  // currencies, not the payment-method lines counted in toleranceTHB above
  // (they can differ, e.g. paid in THB, change given in LAK). Mirrors
  // ShippingPaymentDialog.jsx's changeToleranceTHB.
  const changeToleranceTHB = changeLines
    .filter((l) => l.amount)
    .reduce((sum, l) => sum + halfRoundingStepTHB(l.currency, rate), 0) + EPSILON_THB;
  const changeMismatch = changeDueTHB > 0 && Math.abs(changeLinesTHB - changeDueTHB) > changeToleranceTHB;

  // While amount due is in auto mode (empty/0, not typed into directly), keep
  // it in sync with whatever's been entered across the payment-method tiles,
  // converted into the due amount's own currency. Prefer a clean whole-unit
  // figure but only when that rounding still lands within toleranceTHB — the
  // same slack the backend independently allows — so this never rounds to
  // something the backend would then reject as under/overpaid. Falls back to
  // the precise (cent-accurate) conversion otherwise.
  useEffect(() => {
    if (!dueAuto || !rate) return;
    if (paidTHB <= 0) { setAmount((prev) => (prev === '' ? prev : '')); return; }
    const whole = roundedWholeFromTHB(paidTHB, currency, rate);
    const wholeSafe = Math.abs(toTHB(whole, currency, rate) - paidTHB) <= toleranceTHB;
    const next = String(wholeSafe ? whole : roundedFromTHB(paidTHB, currency, rate));
    setAmount((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueAuto, paidTHB, currency, rate, toleranceTHB]);

  // Recompute the auto-filled last change line whenever the change due shifts.
  useEffect(() => {
    if (changeDueTHB <= 0) return;
    setChangeLines((lines) => recomputeAutoLine(lines, changeDueTHB, rate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeDueTHB, rate]);

  // Remaining due in `curr`, ignoring line `skipIdx` of `which` (the one being
  // filled) so it never counts against itself; -1 for a brand-new line. Staff
  // advance is exclusive with cash/bank, so the other methods contribute 0 there.
  function prefillFor(which, skipIdx, curr) {
    const sumOthers = (key, m) => (m.enabled
      ? m.lines.reduce((s, l, i) => (key === which && i === skipIdx ? s : s + toTHB(parseFloat(l.amount) || 0, l.currency, rate)), 0)
      : 0);
    const otherTHB = sumOthers('cash', cash) + sumOthers('bank', bank) + sumOthers('staffAdvance', staffAdvance);
    return rate ? roundedFromTHB(Math.max(dueTHB - otherTHB, 0), curr, rate) : 0;
  }

  const setterFor = (which) => (which === 'cash' ? setCash : which === 'bank' ? setBank : setStaffAdvance);
  const updateLine = (which, idx, patch) =>
    setterFor(which)((s) => ({ ...s, lines: s.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));

  function toggleMethod(which) {
    const state = which === 'cash' ? cash : which === 'bank' ? bank : staffAdvance;
    const enabling = !state.enabled;
    if (enabling) {
      if (which === 'staffAdvance') { setCash(emptyMethod()); setBank(emptyMethod()); }
      else if (staffAdvance.enabled) setStaffAdvance(emptyMethod());
    }
    const first = state.lines[0];
    setterFor(which)({
      ...state,
      enabled: enabling,
      lines: enabling ? [{ ...first, amount: String(prefillFor(which, 0, first.currency)) }, ...state.lines.slice(1)] : state.lines,
    });
  }

  function setMethodCurrency(which, idx, c) {
    updateLine(which, idx, { currency: c, amount: String(prefillFor(which, idx, c)) });
  }
  function addLine(which) {
    const state = which === 'cash' ? cash : which === 'bank' ? bank : staffAdvance;
    const c = CURRENCIES.find((cur) => !state.lines.some((l) => l.currency === cur)) || 'THB';
    const amount = String(prefillFor(which, -1, c));
    setterFor(which)((s) => ({ ...s, lines: [...s.lines, { ...emptyLine(c), amount }] }));
  }
  function removeLine(which, idx) {
    setterFor(which)((s) => ({ ...s, lines: s.lines.filter((_, i) => i !== idx) }));
  }

  const updateChangeLine = (idx, field, value) => {
    setChangeLines((lines) => {
      const next = lines.map((l, i) => (i === idx ? { ...l, [field]: value, ...(field === 'amount' ? { auto: false } : {}) } : l));
      return recomputeAutoLine(next, changeDueTHB, rate);
    });
  };
  const addChangeLine = () => setChangeLines((lines) => recomputeAutoLine([...lines, emptyChangeLine()], changeDueTHB, rate));
  const removeChangeLine = (idx) =>
    setChangeLines((lines) => {
      const filtered = lines.filter((_, i) => i !== idx);
      return recomputeAutoLine(filtered.length ? filtered : [emptyChangeLine()], changeDueTHB, rate);
    });

  const handleSubmit = async () => {
    if (!amount || amt <= 0) { setError(t('wallet.amountRequired')); return; }
    if (!anyMethodEnabled) { setError(t('wallet.admin.methodRequired')); return; }
    if (cash.enabled && !stationId) { setError(t('wallet.admin.stationRequired')); return; }
    const incomplete = [cash, bank, staffAdvance].some((m) => m.enabled && m.lines.some((l) => !l.amount || !l.file));
    if (incomplete) { setError(t('wallet.admin.evidenceRequired')); return; }
    const anyForeignCurrency = [...methodCurrencies(cash), ...methodCurrencies(bank), ...methodCurrencies(staffAdvance)]
      .some((c) => c !== currency);
    if ((anyForeignCurrency || changeDueTHB > 0) && !rate) { setError(t('wallet.admin.exchangeRateUnavailable')); return; }

    let validChangeLines = [];
    if (changeDueTHB > 0) {
      validChangeLines = changeLines.filter((l) => l.amount && parseFloat(l.amount) > 0);
      if (validChangeLines.length === 0) { setError(t('wallet.admin.changeLinesRequired')); return; }
      if (validChangeLines.some((l) => l.source === 'cash') && !cash.enabled) { setError(t('wallet.admin.changeCashRequiresCash')); return; }
      if (changeMismatch) { setError(t('wallet.admin.changeMismatchError')); return; }
    } else if (underpaid) {
      setError(t('wallet.admin.underpaid')); return;
    }

    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      fd.append('customerId', account.customerId);
      fd.append('amount', amt);
      fd.append('currency', currency);
      fd.append('description', description);

      const lines = [];
      const methodStates = [
        { key: 'cash', state: cash },
        { key: 'bank', state: bank },
        { key: 'staffAdvance', state: staffAdvance },
      ];
      methodStates.forEach(({ key, state }) => {
        if (!state.enabled) return;
        state.lines.forEach((l) => {
          const idx = lines.length;
          lines.push({
            method: METHOD_META[key].backendMethod,
            currency: l.currency,
            amount: parseFloat(l.amount) || 0,
            ...(key === 'cash' ? { stationId } : {}),
            evidenceField: `evidence_${idx}`,
          });
          fd.append(`evidence_${idx}`, l.file);
        });
      });
      fd.append('lines', JSON.stringify(lines));

      if (changeDueTHB > 0) {
        fd.append('changeLines', JSON.stringify(validChangeLines.map((l) => ({ source: l.source, currency: l.currency, amount: parseFloat(l.amount) }))));
      }

      await adminCreateCashPayment(fd);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const allocTile = (label, value, warn) => (
    <Box sx={{
      flex: 1, minWidth: 0, p: 1.5, borderRadius: '14px',
      border: `1px solid ${warn ? alpha(theme.palette.warning.main, 0.4) : BORDER}`,
      background: warn ? alpha(theme.palette.warning.main, 0.06) : paper,
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase' }} color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 18, mt: 0.5 }} color={warn ? 'warning.main' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );

  const methodTile = (which, state, label, IconCmp) => {
    const on = state.enabled;
    const disabledTile = which === 'staffAdvance' ? (cash.enabled || bank.enabled) && !on : staffAdvance.enabled && !on;
    return (
      <Box
        key={which}
        sx={{
          border: `1.5px solid ${on ? alpha(accent, 0.5) : BORDER}`,
          background: on ? tint : paper,
          borderRadius: '16px', overflow: 'hidden', opacity: disabledTile ? 0.5 : 1,
          transition: 'border-color .15s, background .15s',
        }}
      >
        <Stack
          direction="row" alignItems="center" justifyContent="space-between"
          onClick={() => !disabledTile && toggleMethod(which)}
          sx={{ cursor: disabledTile ? 'not-allowed' : 'pointer', px: 1.75, py: 1.5, userSelect: 'none' }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{
              width: 36, height: 36, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: on ? `linear-gradient(135deg, ${accentLight}, ${accentDark})` : alpha(accent, 0.1),
              color: on ? '#fff' : accent,
            }}>
              <IconCmp sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }} color={on ? 'secondary.main' : 'text.primary'}>{label}</Typography>
              {!on && <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t('wallet.admin.tapToAdd')}</Typography>}
            </Box>
          </Stack>
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${on ? accent : alpha(theme.palette.text.secondary, 0.4)}`,
            background: on ? accent : 'transparent',
          }}>
            {on && <CheckRounded sx={{ fontSize: 15, color: '#fff' }} />}
          </Box>
        </Stack>

        {on && (
          <Box sx={{ px: 1.75, pb: 1.75, pt: 0.25 }}>
            {state.lines.map((line, idx) => (
              <Grid container spacing={1} key={idx} sx={idx > 0 ? { mt: 0.5, pt: 1, borderTop: `1px dashed ${alpha(accent, 0.4)}` } : undefined}>
                <Grid item xs={state.lines.length > 1 ? 5.5 : 6}>
                  <TextField select fullWidth size="small" label={t('wallet.currency')} value={line.currency} onChange={(e) => setMethodCurrency(which, idx, e.target.value)}>
                    {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={state.lines.length > 1 ? 5.5 : 6}>
                  <TextField
                    fullWidth size="small" type="number" label={t('wallet.amount')} value={line.amount}
                    onChange={(e) => updateLine(which, idx, { amount: e.target.value })}
                  />
                </Grid>
                {state.lines.length > 1 && (
                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton size="small" color="error" onClick={() => removeLine(which, idx)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <EvidenceUploadButton
                    file={line.file}
                    onChange={(e) => updateLine(which, idx, { file: e.target.files[0] || null })}
                    label={t('wallet.admin.evidenceRequired')}
                  />
                </Grid>
              </Grid>
            ))}
            {state.lines.length < CURRENCIES.length && (
              <Button size="small" variant="text" color="secondary" startIcon={<Add fontSize="small" />} onClick={() => addLine(which)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>
                {t('pickup.payment.addPayment')}
              </Button>
            )}
            {which === 'cash' && (
              <TextField select fullWidth required size="small" sx={{ mt: 1.5 }} label={t('wallet.admin.selectStation')} value={stationId} onChange={(e) => setStationId(e.target.value)}>
                {stationList.map((st) => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
              </TextField>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontWeight: 800 }}>
        <Box>
          {t('wallet.admin.topUp')}
          {account && (
            <Typography variant="caption" color="text.secondary" display="block">
              {account.id} — {account.customer?.name}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>{t('wallet.admin.cashPaymentNote')}</Alert>

        {/* Amount due — hero */}
        <Box sx={{
          borderRadius: '18px', p: '18px 20px', color: '#fff', mb: 2.5,
          background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
          boxShadow: `0 10px 24px -12px ${alpha(accent, 0.7)}`,
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', opacity: 0.85 }}>
            {t('wallet.admin.amountDue')}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.25 }}>
            <TextField
              variant="standard" type="number" value={amount}
              onChange={(e) => {
                const v = e.target.value;
                setAmount(v);
                // Typing a real amount takes it out of auto mode; clearing it
                // back to empty/0 re-enables auto-calc from the payment methods.
                setDueAuto(v === '' || parseFloat(v) === 0);
              }}
              placeholder="0"
              InputProps={{ disableUnderline: true, sx: { fontFamily: MONO, fontWeight: 800, fontSize: 30, color: '#fff' } }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={0.5}>
              {CURRENCIES.map((c) => (
                <Box
                  key={c} onClick={() => setCurrency(c)}
                  sx={{
                    px: 1.25, py: 0.6, borderRadius: '999px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    background: currency === c ? '#fff' : 'rgba(255,255,255,0.16)',
                    color: currency === c ? accentDark : 'rgba(255,255,255,0.85)',
                  }}
                >
                  {c}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>

        <Box sx={sectionLabelSx}>{t('wallet.admin.paymentMethods')}</Box>
        <Stack spacing={1.25} sx={{ mb: 2 }}>
          {methodTile('cash', cash, t(METHOD_META.cash.labelKey), METHOD_META.cash.icon)}
          {methodTile('bank', bank, t(METHOD_META.bank.labelKey), METHOD_META.bank.icon)}
          {methodTile('staffAdvance', staffAdvance, t(METHOD_META.staffAdvance.labelKey), METHOD_META.staffAdvance.icon)}
        </Stack>

        {anyMethodEnabled && (
          <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
            {allocTile(t('wallet.admin.paidNow'), `${CURRENCY_SYMBOLS.THB}${displayPaidTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}`)}
            {allocTile(t('wallet.admin.changeDueLabel'), `${CURRENCY_SYMBOLS.THB}${changeDueTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, changeDueTHB > 0)}
          </Stack>
        )}

        {underpaid && changeDueTHB === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('wallet.admin.underpaid')}</Alert>
        )}

        {changeDueTHB > 0 && (
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: '14px',
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            background: alpha(theme.palette.warning.main, 0.05),
          }}>
            <Typography sx={{ fontSize: 11.5, mb: 1 }} color="text.secondary">{t('wallet.admin.giveChangeHint')}</Typography>
            <Stack spacing={1}>
              {changeLines.map((line, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <TextField
                    select size="small" value={line.source} onChange={(e) => updateChangeLine(idx, 'source', e.target.value)}
                    sx={{ width: 140, flexShrink: 0 }}
                  >
                    {cash.enabled && <MenuItem value="cash">{t('wallet.admin.changeCash')}</MenuItem>}
                    <MenuItem value="wallet_credit">{t('wallet.admin.changeWallet')}</MenuItem>
                  </TextField>
                  <TextField
                    select size="small" value={line.currency} onChange={(e) => updateChangeLine(idx, 'currency', e.target.value)}
                    sx={{ width: 88, flexShrink: 0 }}
                  >
                    {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                  <TextField
                    fullWidth size="small" type="number" label={t('wallet.amount')} value={line.amount}
                    onChange={(e) => updateChangeLine(idx, 'amount', e.target.value)}
                  />
                  <IconButton size="small" onClick={() => removeChangeLine(idx)} disabled={changeLines.length === 1}>
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" variant="text" color="secondary" startIcon={<Add fontSize="small" />} onClick={addChangeLine} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>
              {t('wallet.admin.addChangeLine')}
            </Button>
            {changeMismatch && (
              <Typography sx={{ fontSize: 11, mt: 0.5 }} color="error.main">{t('wallet.admin.changeMismatch')}</Typography>
            )}
          </Box>
        )}

        <TextField
          fullWidth multiline rows={2} label={t('wallet.description')} value={description}
          onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes..."
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={loading || !amount || !anyMethodEnabled}
          startIcon={loading ? <CircularProgress size={18} /> : <Payments fontSize="small" />}
        >
          {loading ? t('wallet.admin.submittingRequest') : t('wallet.admin.topUp')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletTopupDialog;
